/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../AlignAPI',
	'../../../../../lib/@waca/dashboard-common/dist/ui/interaction/Utils',
	'../../../../../app/nls/StringResources',
	'jquery',
	'../../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(APIFactory, AlignAPI, utils, stringResources, $, ContentActionsProviderAPI) {

	class AlignAction {

		constructor({ features }) {
			if(features) {
				this.dashboard = features.API;
				features.ContentActions.registerProvider('align', this.getAPI());
				this._icons = features.Icons;
			}
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [AlignAPI, ContentActionsProviderAPI]);
			}
			return this.api;
		}

		getLifeCycleHandlers() {
			return [{
				name: 'post:dashboard.initialize',
				action: this.postDashboardInitialize.bind(this)
			}];
		}

		postDashboardInitialize() {
			if(this.dashboard) {
				this.controller = this.dashboard.getFeature('InteractionController.internal');
			}
			return Promise.resolve();
		}

		destroy() {
			this.dashboard = null;
			this.controller = null;
		}

		getNodes(idList) {
			return utils.getNodes(this.controller, idList);
		}

		_isEnabled(idList) {
			return idList.length > 1 && this.dashboard.getMode() === this.dashboard.MODES.EDIT;
		}

		getContentActionList(idList) {
			if (this._isEnabled(idList)) {
				return [{
					name: 'align',
					label: stringResources.get('toolbarActionAlign'),
					icon: this._icons.getIcon('align-horizontally').id,
					type: 'NextView',
					viewModule: 'dashboard-core/js/features/dashboard/actions/alignAction/impl/AlignDialog',
					viewOptions: {
						height: 290,
						width: 110, // those are hints only; for the placement of the toolbar.
						actions: {
							apply: this.applyAlignment.bind(this, idList)
						}
					}
				}];
			}
			return [];
		}

		applyAction(selection) {
			if (selection && selection.length && selection[0].name) {
				const position = selection[0].name;
				const idList = selection[0].layoutIds;
				this.applyAlignment(idList, position);
			}
		}

		getWidgetBoxes(node) {
			const widgetPos = utils.position(node);
			const widgetSize = utils.widgetSize(node);
			let result = {
				boundingBox: $(node).position(),
				widgetBox: {
					top: widgetPos.top,
					left: widgetPos.left,
					width: widgetSize.width,
					height: widgetSize.height
				}
			};
			const boundingRect = node.getBoundingClientRect();
			result.boundingBox.width = boundingRect.width;
			result.boundingBox.height = boundingRect.height;
			return result;
		}

		// TODO this function is called multiple times with same args when click align action
		// we need a better way to avoid this, because we only need to do once when click align action
		applyAlignment(idList, position) {
			const alignmentFns = {
				left: this.getLeftAlignmentStyle,
				center: this.getCenterAlignmentStyle,
				right: this.getRightAlignmentStyle,
				top: this.getTopAlignmentStyle,
				middle: this.getMiddleAlignmentStyle,
				bottom: this.getBottomAlignmentStyle
			};
			let widgetBoxes = [];
			const selectedNodes = this.getNodes(idList);
			const widgetBox = this.getWidgetBoxes(selectedNodes[0]);
			widgetBoxes.push(widgetBox);
			let bounds = {
				top: widgetBox.boundingBox.top,
				left: widgetBox.boundingBox.left,
				bottom: widgetBox.boundingBox.top + widgetBox.boundingBox.height,
				right: widgetBox.boundingBox.left + widgetBox.boundingBox.width
			};
			for (let i=1; i<selectedNodes.length; i++) {
				const widgetBox = this.getWidgetBoxes(selectedNodes[i]);
				widgetBoxes.push(widgetBox);
				bounds.top = Math.min(bounds.top, widgetBox.boundingBox.top);
				bounds.left = Math.min(bounds.left, widgetBox.boundingBox.left);
				bounds.bottom = Math.max(bounds.bottom, widgetBox.boundingBox.top + widgetBox.boundingBox.height);
				bounds.right = Math.max(bounds.right, widgetBox.boundingBox.left + widgetBox.boundingBox.width);
			}

			let positionUpdateArray = [];
			for (let i=0; i<selectedNodes.length; i++) {
				const updatedStyle = alignmentFns[position](bounds, widgetBoxes[i], selectedNodes[i]);
				if(updatedStyle) {
					positionUpdateArray.push({
						style: updatedStyle,
						id: selectedNodes[i]._layout.model.id
					});
				}
			}
			if (positionUpdateArray.length) {
				const transactionApi = this.dashboard.getFeature('Transaction');
				const transactionToken = transactionApi.startTransaction();
				positionUpdateArray.forEach(positionUpdate => {
					const content = this.dashboard.getCanvas().getContent(positionUpdate.id);
					Object.keys(positionUpdate.style).forEach(propertyName => {
						content.setPropertyValue(propertyName, positionUpdate.style[propertyName], transactionToken);
					});
				});
				transactionApi.endTransaction(transactionToken);
			}
		}

		getLeftAlignmentStyle(bounds, widgetBox, node) {
			let leftPos = bounds.left;
			if (Math.round(widgetBox.boundingBox.left) !== Math.round(widgetBox.widgetBox.left)) {
				const offset = widgetBox.boundingBox.left - widgetBox.widgetBox.left;
				leftPos = leftPos - offset;
			}
			//Sanity check the position against the minimum
			leftPos = Math.max(leftPos, node._layout.parentLayout.getMinimumLeft());
			return {
				left: leftPos + 'px'
			};
		}

		getCenterAlignmentStyle(bounds, widgetBox, node) {
			const centerLine = bounds.left + ((bounds.right - bounds.left)/2);
			let leftPos = centerLine - (widgetBox.widgetBox.width/2);
			if (Math.round(widgetBox.boundingBox.left) !== Math.round(widgetBox.widgetBox.left)) {
				const offset = (widgetBox.boundingBox.left + (widgetBox.boundingBox.width/2)) - (widgetBox.widgetBox.left + (widgetBox.widgetBox.width/2));
				leftPos = leftPos - offset;
			}
			//Sanity check the position against the minimum
			leftPos = Math.max(leftPos, node._layout.parentLayout.getMinimumLeft());
			return {
				left: leftPos + 'px'
			};
		}

		getRightAlignmentStyle(bounds, widgetBox, node) {
			let leftPos = bounds.right - widgetBox.widgetBox.width;
			if (Math.round(widgetBox.boundingBox.width) !== Math.round(widgetBox.widgetBox.width)) {
				const offset = (widgetBox.boundingBox.left + widgetBox.boundingBox.width) - (widgetBox.widgetBox.left + widgetBox.widgetBox.width);
				leftPos = leftPos - offset;
			}
			//Sanity check the position against the minimum
			leftPos = Math.max(leftPos, node._layout.parentLayout.getMinimumLeft());
			return {
				left: leftPos + 'px'
			};
		}

		getTopAlignmentStyle(bounds, widgetBox, node) {
			let topPos = bounds.top;
			if (Math.round(widgetBox.boundingBox.top) !== Math.round(widgetBox.widgetBox.top)) {
				const offset = widgetBox.boundingBox.top - widgetBox.widgetBox.top;
				topPos = topPos - offset;
			}
			//Sanity check the position against the minimum
			topPos = Math.max(topPos, node._layout.parentLayout.getMinimumTop());
			return {
				top: topPos + 'px'
			};
		}

		getMiddleAlignmentStyle(bounds, widgetBox, node) {
			const middleLine = bounds.top + ((bounds.bottom - bounds.top)/2);
			let topPos = middleLine - (widgetBox.widgetBox.height/2);
			if (Math.round(widgetBox.boundingBox.top) !== Math.round(widgetBox.widgetBox.top)) {
				const offset = (widgetBox.boundingBox.top + (widgetBox.boundingBox.height/2)) - (widgetBox.widgetBox.top + (widgetBox.widgetBox.height/2));
				topPos = topPos - offset;
			}
			//Sanity check the position against the minimum
			topPos = Math.max(topPos, node._layout.parentLayout.getMinimumTop());
			return {
				top: topPos + 'px'
			};
		}

		getBottomAlignmentStyle(bounds, widgetBox, node) {
			let topPos = bounds.bottom - widgetBox.widgetBox.height;
			if (Math.round(widgetBox.boundingBox.height) !== Math.round(widgetBox.widgetBox.height)) {
				const offset = (widgetBox.boundingBox.top + widgetBox.boundingBox.height) - (widgetBox.widgetBox.top + widgetBox.widgetBox.height);
				topPos = topPos - offset;
			}
			//Sanity check the position against the minimum
			topPos = Math.max(topPos, node._layout.parentLayout.getMinimumTop());
			return {
				top: topPos + 'px'
			};
		}
	}

	return AlignAction;

});
